import './KeywordList.scss';

import React, { ChangeEvent, FC, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { AppContext } from 'components/AppContext/AppContext';
import TableSearch from 'components/TableSearch/TableSearch';
import { LogLevel } from 'models/LogLevel';
import { ISeoKeyword, TargetKeyword } from 'models/TargetKeyword';
import { TopicModel } from 'models/TopicModel';
import { Button, Col, Form, Row } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';

interface KeywordListProps {
  onSelectKeywords: (keywords: TargetKeyword[], selectedSubtopics: TopicModel[]) => void,
  isLoading: boolean,
  setIsLoading: (isLoading: boolean) => void,
  selectedSubtopics: TopicModel[],
  keywords: TargetKeyword[],
  activeStep: number;
}

const thisStepNumber = 3;

const KeywordList: FC<KeywordListProps> = ({ onSelectKeywords, isLoading, setIsLoading, selectedSubtopics, keywords, activeStep }) => {

  const { sendLog } = useContext(AppContext);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState<Set<number>>(new Set());
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<TargetKeyword[]>([]);

  useEffect(() => {
    setSelectedKeywords(keywords);
  }, [keywords]);

  const handleKeywordSelected = (keywordId: number, checked: boolean) => {
    setSelectedKeywordIds((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(keywordId);
      } else {
        newSet.delete(keywordId);
      }
      return newSet;
    });
    sendLog(`The keyword id ${keywordId} was checked: ${checked}`, LogLevel.info);
  }

  const handleSelectKeywords = async () => {
    setIsLoading(true);
    const filteredKeywords = keywords.filter((keyword) => selectedKeywordIds.has(keyword.id));
    try {
      await onSelectKeywords(filteredKeywords, selectedSubtopics);
    } finally {
      setIsLoading(false);
    }
  }

  const getKeywordColor = (difficulty: number) => {
    if (difficulty < 20) {
      return 'green';
    }
    if (difficulty < 40) {
      return 'orange';
    }
    return 'red';
  };

  const applySearch = (terms: string | undefined) => {
    if (!terms) {
      setSelectedKeywords(keywords);
      return;
    }
    const updatedKeywords = keywords.filter(
      (keyword) => keyword.seoKeyword.toLowerCase().includes(terms?.toLowerCase() ?? '')
    );
    setSelectedKeywords(updatedKeywords);
  };

  const [filterText, setFilterText] = useState('');
  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText('');
        applySearch(undefined);
      }
    };

    const onFilter = (e: SyntheticEvent) => {
      e.preventDefault();
      applySearch(filterText);
    };

    const onSearchTextChanged = (e: ChangeEvent<HTMLInputElement>) => {
      setFilterText(e.target.value);
    };

    return (
      <div className="action-panel">
        <Row>
          <Col md={6} xs={6} className="g-0 text-right">
            <TableSearch
              onFilter={onFilter}
              onClear={handleClear}
              filterPropertyName="Keyword"
              searchText={filterText}
              onSearchTextChanged={onSearchTextChanged}
            />
          </Col>
        </Row>
      </div>
    );
  }, [filterText, resetPaginationToggle]);

  const columns: TableColumn<TargetKeyword>[] = [
    {
      name: 'Actions',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (row: any) => (
        <Form.Check
          type="checkbox"
          id={`keyword-${row.id}`}
          key={row.id}
          checked={selectedKeywordIds.has(row.id)}
          onChange={(e) => handleKeywordSelected(row.id, e.target.checked)}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: false,
      button: true,
    } as TableColumn<TargetKeyword>,
    {
      name: 'Keyword',
      selector: (row: ISeoKeyword) => row.seoKeyword,
      type: 'seoKeyword',
      sortable: true,
      width: '300px',
    } as TableColumn<TargetKeyword>,
    {
      name: 'Search Volume',
      selector: (row: ISeoKeyword) => row.seoMonthlySearchVolume,
      type: 'seoMonthlySearchVolume',
      sortable: true,
      width: '150px',
    } as TableColumn<TargetKeyword>,
    {
      name: 'Difficulty',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (row: any) => (
        <span style={{ color: getKeywordColor(row.seoDifficulty!), fontWeight: 'bold' }}>
          {row.seoDifficulty}
        </span>
      ),
      type: 'seoDifficulty',
      sortable: true,
      sortFunction: (a: ISeoKeyword, b: ISeoKeyword) => (a.seoDifficulty || 0) - (b.seoDifficulty || 0),
      width: '150px'
    } as TableColumn<TargetKeyword>
  ];

  const renderKeywords = () => (
    <div className="data-collection">
      <DataTable
        columns={columns}
        defaultSortFieldId="2"
        defaultSortAsc={false}
        className="tbl-keywords"
        data={selectedKeywords}
        pagination
        paginationTotalRows={selectedKeywords.length ?? 0}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        persistTableHead
      />
    </div>
  );

  return (
    <div className="identified-keywords" style={{ display: activeStep === thisStepNumber ? 'block' : 'none' }}>
      <h2>Select keywords to generate article ideas for:</h2>
      {renderKeywords()}
      <div className="d-flex justify-content-end">
        <Button variant="primary" onClick={handleSelectKeywords} disabled={isLoading}>
          <i className="bi bi-plus-circle" />
          Generate Article Ideas
        </Button>
      </div>
    </div>
  );
}

export default KeywordList;
